/*
 * Copyright Innov'ATM all rights reserved.
 * This software is the property of Innov'ATM and may not be used in any manner except under a
 * license agreement signed with Innov'ATM.
 */

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import { format, isBefore, startOfDay, subDays } from 'date-fns';
import { useFormikContext } from 'formik';
import React, { ChangeEvent, memo, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { FlightTimeType, Resource, ResourceType, Towing } from '../../../../../../../backend';
import { useIntlValue } from '../../../../../../../contexts/IntlContext';
import { getTwoDaysAgo } from '../../../../../../../utils/utils';
import { Select } from '../../../../../../form/Select/Select';
import { convertTimestampToDatetimeString, localeToShortDateFormat } from '../../../../../../time/time.utils';
import { selectSx } from '../ResourcesSelect.style';
import { TurnaroundFormValues } from '../TurnaroundForm.utils';
import { StyledDatetimeContainer, StyledTowingContainer } from './ParkingTowingInput.style';

export const ParkingTowingInput = memo(function ParkingTowingInput({
    parkings,
    towings,
    index,
    disabled,
}: {
    parkings: Resource[];
    towings: Towing[];
    index: number;
    disabled?: boolean;
}) {
    const { values, setValues, errors } = useFormikContext<TurnaroundFormValues>();
    const [initialTowing] = useState(towings?.[index]);
    const selectedParking = towings?.[index]?.parking;
    const selectedDatetimeString = convertTimestampToDatetimeString(towings?.[index]?.time?.time);
    const datetimeInputRef = useRef<HTMLInputElement | null>(null);
    const locale = useIntlValue('locale');
    const twoDaysAgo = getTwoDaysAgo();

    const isTowingDisabled =
        disabled || (initialTowing?.time.type === FlightTimeType.T && isBefore(initialTowing.time.time, twoDaysAgo));

    const updateTowing = useCallback(
        (newTowings: Towing[]) => {
            setValues({
                ...values,
                turnaround: {
                    ...values.turnaround,
                    arrivalFlight: {
                        ...values.turnaround.arrivalFlight,
                        towings: newTowings,
                    },
                },
                resourceTypes: [...values.resourceTypes, ResourceType.PARKING],
            });
        },
        [setValues, values],
    );

    const handleTowingParkingChange = useCallback(
        (event: SelectChangeEvent) => {
            const newTowings = [...towings];
            newTowings[index].parking = event.target.value;
            updateTowing(newTowings);
        },
        [index, towings, updateTowing],
    );

    const handleTowingTimeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (!event.target.value) return;
            const newTowings = [...towings];
            newTowings[index].time.time = Date.parse(event.target.value);
            updateTowing(newTowings);
        },
        [index, towings, updateTowing],
    );

    const earliestPossibleTime =
        index >= 1
            ? towings?.[index - 1]?.time?.time
            : values.turnaround?.arrivalFlight?.actualResourceOccupations['PARKING']?.occupationStart?.time;
    const minDate = convertTimestampToDatetimeString(
        isBefore(earliestPossibleTime, twoDaysAgo)
            ? startOfDay(subDays(new Date(), 1)).getTime()
            : earliestPossibleTime,
    );

    let maxDate = '';
    if (towings && towings.length > index + 1) {
        maxDate = convertTimestampToDatetimeString(towings[index + 1]?.time?.time);
    } else if (values.turnaround?.departureFlight?.actualResourceOccupations['PARKING']?.occupationEnd?.time) {
        maxDate = convertTimestampToDatetimeString(
            values.turnaround.departureFlight.actualResourceOccupations['PARKING'].occupationEnd.time,
        );
    }

    return (
        <StyledTowingContainer errors={!!errors[`turnaround.arrivalFlight.towings[${index}].time`]}>
            <StyledDatetimeContainer
                className={isTowingDisabled ? 'disabled' : ''}
                errors={!!errors[`turnaround.arrivalFlight.towings[${index}].time`]}
            >
                <span className={'datepicker-date-display'}>
                    {format(towings?.[index]?.time?.time, localeToShortDateFormat[locale])}
                </span>
                <CalendarMonthIcon className={'datepicker-toggle-button'} />
                <span className={'datepicker-time-display'}>{format(towings?.[index]?.time?.time, 'HH:mm')}</span>
                <input
                    type="datetime-local"
                    value={selectedDatetimeString}
                    onChange={handleTowingTimeChange}
                    name={`turnaround.arrivalFlight.towings[${index}].time.time`}
                    min={minDate}
                    max={maxDate}
                    required={true}
                    disabled={isTowingDisabled}
                    className={'datepicker-input'}
                    ref={datetimeInputRef}
                />
            </StyledDatetimeContainer>
            <Select
                value={selectedParking || ''}
                label={<>&nbsp;</>}
                FormControlProps={{
                    sx: { ...selectSx, maxWidth: '100px', minWidth: '75px' },
                }}
                onChange={handleTowingParkingChange}
                disabled={isTowingDisabled}
                name={`turnaround.arrivalFlight.towings[${index}].parking`}
                className={'parking-towing-select'}
            >
                {!parkings?.find(r => r.name === selectedParking) && (
                    <MenuItem value={selectedParking} disabled={true}>
                        <em>{selectedParking}</em>
                    </MenuItem>
                )}
                <MenuItem value={''}>
                    <em>-</em>
                </MenuItem>
                {parkings?.map(({ name }) => (
                    <MenuItem key={name} value={name}>
                        <FormattedMessage id={`turnaround.popin.field`} values={{ field: name }} />
                    </MenuItem>
                ))}
            </Select>
        </StyledTowingContainer>
    );
});
